import { styled } from "@stitches/react";
import TaoEditorContent from "../TaoEditorContent";
import { useTaoDepartments } from "@/hooks/use-tao-departments";
import { useTaoGenuses } from "@/hooks/use-tao-genuses";
import {
  TaoDepartmentsList,
  TaoList,
  TaoProvidersList,
} from "@/types/tao-routing";
import { useTaoOrderTypes } from "@/hooks/use-tao-order-types";
import { useUsers } from "@/hooks/use-users";
import { useTaoProviders } from "@/hooks/use-tao-providers";
import Loader from "../Loader";
import { useLocation } from "react-router-dom";
import { useIndividualTaoData } from "@/hooks/use-individual-tao-data";

const TaoCreateWrapper = styled("div", {
  display: "flex",
  flexFlow: "column",
  justifyContent: "center",
  width: "610px",
  maxWidth: "610px", // Prevents the width from exceeding 610px
  minWidth: "610px",

  "& .fe_c_stepper__item::after": {
    backgroundColor: "White",
    content: '""',
    display: "block",
    position: "absolute",

    right: "0", // Adjusts the position to avoid overriding the spacing
    width: "25px", // Ensures the width matches the intended gap
    maxWidth: "25px", // Prevents the width from exceeding 25px
    minWidth: "25px", // Ensures the width is at least 25px
    height: "2px",
  },
  "& .fe_c_stepper__item": {
    width: "96px", // Adds spacing between each stepper item
  },

  "& .fe_c_stepper__item:last-child": {
    marginBottom: "0", // Removes margin for the last stepper item
  },
});

const HeaderWrapper = styled("div", {
  alignSelf: "center",

  "& h1": {
    fontWeight: "bold",
    fontSize: "34px",
  },
});

interface SelectedTaoList {
  selectedTaoData: TaoList[];
  isDuplicate: boolean;
  isEdit?: boolean;
}

const TaoEditor = () => {
  const { data: departments, isLoading: isDepartmentsLoading } =
    useTaoDepartments();
  const { data: genusList, isLoading: isGenusLoading } = useTaoGenuses();
  const { data: orderTypes, isLoading: isOrderTypesLoading } =
    useTaoOrderTypes();
  const { data: buckets = [], isLoading: isBucketsLoading } = useUsers();
  const { data: providers, isLoading: isProvidersLoading } = useTaoProviders();
  const { selectedTaoData, isDuplicate, isEdit } = (useLocation()
    .state as SelectedTaoList) ?? { selectedTaoData: [], isDuplicate: false };
  const {
    data: individualTaoData,
    isLoading: isFetchingTaoData,
    isRefetching: isRefetchingTaoData,
  } = useIndividualTaoData(
    selectedTaoData?.length ? selectedTaoData[0].taoId : undefined,
  );

  const departmentsNoDups = new Set([
    ...(departments?.departments?.map(
      (department: TaoDepartmentsList) => department.name,
    ) ?? []),
    ...(isDuplicate
      ? individualTaoData?.departmentMap.map((department) => department.name) ??
        []
      : []),
  ]);

  const rawProviders = providers?.providers;
  const providerList = Array.isArray(rawProviders) ? rawProviders : [];

  // Extract before checking
  const rawProviderMap = individualTaoData?.providerMap;

  const duplicateList =
    isDuplicate && Array.isArray(rawProviderMap) ? rawProviderMap : [];

  const ProvidersNoDups = new Set([
    ...providerList.map((p) => p.displayName),
    ...duplicateList.map((p) => p.displayName),
  ]);

  const valuesMap = {
    departments: [...Array.from(departmentsNoDups)],
    providers: [...Array.from(ProvidersNoDups)],
    genus: [...(genusList ?? [])],
    buckets: buckets?.filter((user) => user !== "ALL") ?? [],
    orderTypes: [
      "ALL",
      ...(orderTypes?.map((orderType) => orderType.orderTypeName) ?? []),
    ],
  };

  const fullList = {
    //departments: departments?.departments ?? [],
    departments: [
      ...(departments?.departments ?? []),
      ...(individualTaoData?.departmentMap ?? []),
    ],
    providers: [...(providers?.providers ?? [])],
    orderTypes: [
      ...(orderTypes ?? []),
      {
        orderTypeName: "ALL",
        orderTypeId: "ALL",
        orderGenusName: "ALL",
        orderTypeGroupName: "ALL",
        orderTypeGroupId: 0,
      },
    ],
  };

  return (
    <TaoCreateWrapper>
      {isDuplicate ? (
        <HeaderWrapper>
          <h1>Duplicate TAO {selectedTaoData[0]?.taoId}</h1>
        </HeaderWrapper>
      ) : isEdit ? (
        <HeaderWrapper>
          <h1>Edit TAO {selectedTaoData[0]?.taoId}</h1>
        </HeaderWrapper>
      ) : (
        <HeaderWrapper>
          <h1>Create New TAO</h1>
        </HeaderWrapper>
      )}
      <Loader
        displayText="Loading"
        isLoading={
          isOrderTypesLoading ||
          isBucketsLoading ||
          isDepartmentsLoading ||
          isGenusLoading ||
          isProvidersLoading ||
          isFetchingTaoData ||
          isRefetchingTaoData
        }
      >
        <TaoEditorContent
          selectedTaoDataFromBackend={
            isDuplicate || isEdit ? individualTaoData : undefined
          }
          originalList={valuesMap}
          fullList={fullList}
        />
      </Loader>
    </TaoCreateWrapper>
  );
};

export default TaoEditor;
